// Shared shape helpers for the "custom fields" template data model.
//
// A template stores three JSON blobs:
//   - customSections ("config"): a flat, unordered list of section identities.
//       Section = { guid, name }
//   - customFields ("data"): a flat, unordered list of field-instance links.
//       FieldLink = { guid, customFieldId }
//   - customFieldsLayout ("layout"): the ONLY place hierarchy/order lives — a tree of nodes.
//       Node = { type: "section" | "field", id, children?: [Node] }
//     `id` is the referenced section's or field-link's guid. The top-level array is the ordered
//     list of root sections, rendered as tabs. A section node's `children` may contain more
//     section nodes (rendered as a nested/boxed group — what used to be called a "subsection" is
//     now just a section that isn't at the root of the tree) and/or field nodes. A field node's
//     `children` is only meaningful when that field resolves to a Region type, and holds that
//     Region's own nested items in order.
//
// Order is entirely positional (array index within `children`) — there is no sortOrder number
// anywhere, so adding/moving/removing an item never requires renumbering siblings.
//
// A field's or section's parent is NOT stored on the item itself — it's derived by finding where
// its guid is referenced in the layout tree. Most other parts of the app (quote-taking, export
// generation) don't care about the tree shape at all; they only need a flat, ordered list of
// field placements, which `flattenTemplateFields` derives from the tree on demand.

const genGuid = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

export const parseJsonArray = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

// Parses `customFields` into the flat per-instance link shape: { guid, customFieldId }.
export const normalizeFields = (rawFields) =>
    parseJsonArray(rawFields).map((f) => ({
        guid: String(f?.guid ?? genGuid()),
        customFieldId: f?.customFieldId ?? f?.id ?? null,
    }));

// Parses `customSections` into the flat section identity list: { guid, name }.
export const normalizeSections = (rawSections) =>
    parseJsonArray(rawSections)
        .map((s) => ({
            guid: String(s?.guid ?? genGuid()),
            name: String(s?.name ?? "").trim(),
        }))
        .filter((s) => !!s.name);

// Parses `customFieldsLayout` into the hierarchy tree: [{ type, id, children? }].
const normalizeLayoutNode = (node) => {
    if (!node) return null;
    const type = node.type === "section" ? "section" : node.type === "field" ? "field" : null;
    const id = String(node.id ?? "").trim();
    if (!type || !id) return null;

    const result = { type, id };
    const children = Array.isArray(node.children)
        ? node.children.map(normalizeLayoutNode).filter(Boolean)
        : [];
    if (children.length) result.children = children;
    return result;
};

export const normalizeLayout = (rawLayout) =>
    parseJsonArray(rawLayout).map(normalizeLayoutNode).filter(Boolean);

// Name used for the auto-created catch-all section (see ensureFieldsInLayout below).
const AUTO_INPUTS_SECTION_NAME = "Inputs";

// Repairs a normalized {sections, layout} pair by finding any section identity with no node
// anywhere in the layout tree (e.g. a section created but never inserted into the tree) and
// appending it as a new root (tab) section, in the order the orphaned sections are found.
export const ensureSectionsInLayout = (sections, layout) => {
    const placedSectionGuids = new Set();
    const visit = (nodes) => (nodes || []).forEach((node) => {
        if (node.type === "section") placedSectionGuids.add(node.id);
        if (node.children?.length) visit(node.children);
    });
    visit(layout);

    const orphanedSections = sections.filter((s) => !placedSectionGuids.has(s.guid));
    if (!orphanedSections.length) return { layout, changed: false };

    const newSectionNodes = orphanedSections.map((s) => ({ type: "section", id: s.guid }));
    return { layout: [...layout, ...newSectionNodes], changed: true };
};

// Repairs a normalized {fields, sections, layout} triple by finding any field link with no node
// anywhere in the layout tree (e.g. a template saved before customFieldsLayout existed, or a
// field that otherwise fell out of the tree) and appending it to an "Inputs" section, in the
// order the orphaned fields are found — reusing a root section already named "Inputs" if one
// exists, otherwise creating a new root (tab) section for them. This is what "loading a template"
// means for the custom-fields model: every known field link always ends up somewhere visible.
export const ensureFieldsInLayout = (fields, sections, layout) => {
    const placedFieldGuids = new Set();
    const visit = (nodes) => (nodes || []).forEach((node) => {
        if (node.type === "field") placedFieldGuids.add(node.id);
        if (node.children?.length) visit(node.children);
    });
    visit(layout);

    const orphanedFields = fields.filter((f) => !placedFieldGuids.has(f.guid));
    if (!orphanedFields.length) return { fields, sections, layout, changed: false };

    const sectionNamesByGuid = new Map(sections.map((s) => [s.guid, s.name]));
    const existingInputsRoot = layout.find(
        (node) =>
            node.type === "section" &&
            (sectionNamesByGuid.get(node.id) || "").trim().toLowerCase() === AUTO_INPUTS_SECTION_NAME.toLowerCase()
    );

    const newFieldNodes = orphanedFields.map((f) => ({ type: "field", id: f.guid }));

    if (existingInputsRoot) {
        const nextLayout = layout.map((node) =>
            node === existingInputsRoot
                ? { ...node, children: [...(node.children || []), ...newFieldNodes] }
                : node
        );
        return { fields, sections, layout: nextLayout, changed: true };
    }

    const inputsSectionGuid = genGuid();
    const nextSections = [...sections, { guid: inputsSectionGuid, name: AUTO_INPUTS_SECTION_NAME }];
    const nextLayout = [...layout, { type: "section", id: inputsSectionGuid, children: newFieldNodes }];

    return { fields, sections: nextSections, layout: nextLayout, changed: true };
};

// The single "load a template" entry point: normalizes the three raw blobs and repairs the
// layout so every section identity and field link is placed somewhere (see
// ensureSectionsInLayout and ensureFieldsInLayout). Consumers that need the actual tree (not just
// a flat field list) should use this instead of calling normalizeFields/normalizeSections/
// normalizeLayout separately.
export const resolveEffectiveTemplateState = (rawFields, rawSections, rawLayout) => {
    const fields = normalizeFields(rawFields);
    const sections = normalizeSections(rawSections);
    const layout = normalizeLayout(rawLayout);

    const sectionsRepair = ensureSectionsInLayout(sections, layout);
    const fieldsRepair = ensureFieldsInLayout(fields, sections, sectionsRepair.layout);

    return {
        fields: fieldsRepair.fields,
        sections: fieldsRepair.sections,
        layout: fieldsRepair.layout,
        changed: sectionsRepair.changed || fieldsRepair.changed,
    };
};

// Maps section guid -> section name. Used by consumers that only need display names.
export const buildSectionNameByGuid = (rawSections) => {
    const map = new Map();
    normalizeSections(rawSections).forEach((section) => {
        map.set(section.guid, section.name);
    });
    return map;
};

// Derives a flat, ordered list of field placements — [{ id: customFieldId, guid, sectionGuid,
// sortOrder }] — by walking the layout tree. `sectionGuid` is always the top-level (root/tab)
// section a field ultimately sits under, no matter how deeply it's nested inside further
// sections and/or Regions. `sortOrder` is a global depth-first position, stable enough for
// consumers that just want to sort a section's fields into display order. Used by every consumer
// that doesn't care about the actual tree shape and just wants field placements.
export const flattenTemplateFields = (rawFields, rawSections, rawLayout) => {
    const { fields, layout } = resolveEffectiveTemplateState(rawFields, rawSections, rawLayout);
    const fieldsByGuid = new Map(fields.map((f) => [f.guid, f]));
    const flat = [];
    let counter = 0;

    const walk = (nodes, rootSectionGuid) => {
        (nodes || []).forEach((node) => {
            if (!node) return;

            if (node.type === "section") {
                walk(node.children, rootSectionGuid);
                return;
            }

            const field = fieldsByGuid.get(node.id);
            if (!field) return;

            counter += 1;
            flat.push({
                id: field.customFieldId,
                guid: field.guid,
                sectionGuid: rootSectionGuid,
                sortOrder: counter,
            });

            if (node.children?.length) {
                walk(node.children, rootSectionGuid);
            }
        });
    };

    layout.forEach((rootNode) => {
        if (rootNode.type !== "section") return;
        walk(rootNode.children, rootNode.id);
    });

    return flat;
};
